import React from 'react';
import { Animated, Image, StyleSheet, Text, View } from 'react-native';

import { VaultChestIcon } from '../shared/RelicVaultIcons';
import { usePopIn } from '../shared/useRelicVaultAnimations';
import { COLORS, bodyFont, em, headingFont, panelSurface } from '../shared/theme';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { ALL_GUILDS_ID, guildById, summarizeProgress } from 'utils/relicVault/catalog';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

import { TYPE, FONTS } from 'style/typography';

const ALL_DEF = { name: 'All Relics', title: 'Every Guild', colorHex: '#8B5CF6' };

/** Selected guild's companion art, collection title, progress bar and rarity chips. */
const VaultHeader = ({ catalog, selectedGuildId, currentRelics, isTablet, isPhone }) => {
  const isAll = selectedGuildId === ALL_GUILDS_ID;
  const guild = isAll ? null : guildById(catalog, selectedGuildId);
  const def = guild || ALL_DEF;
  const progress = summarizeProgress(catalog, currentRelics);
  const pop = usePopIn(selectedGuildId, { duration: 300, from: 0.7, overshoot: null });

  const artBox = isTablet ? { width: 90, height: 84 } : { width: 130, height: 112 };
  const guardian = guild ? catalog.guardianByGuildId.get(guild.guildId) : null;
  const aspect =
    guardian?.artWidth && guardian?.artHeight ? guardian.artWidth / guardian.artHeight : 0.7;
  // object-fit: contain inside the art box
  const artSize =
    artBox.width / artBox.height > aspect
      ? { height: artBox.height, width: artBox.height * aspect }
      : { width: artBox.width, height: artBox.width / aspect };

  const title = isAll ? 'ALL RELICS' : `${def.name.toUpperCase()}'S RELIC COLLECTION`;

  return (
    <View
      style={[
        styles.header,
        isTablet && styles.headerTablet,
        isPhone && styles.headerPhone,
        webOnly({ boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }),
      ]}>
      <View style={[styles.companion, artBox]}>
        {guardian?.artUrl && (
          <Animated.View style={pop}>
            <Image
              source={{ uri: guardian.artUrl }}
              resizeMode="contain"
              style={[artSize, webOnly({ filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.35))' })]}
            />
          </Animated.View>
        )}
      </View>

      <View
        style={[
          styles.icon,
          IS_WEB ? { backgroundImage: iconGlowFor(def.colorHex) } : { backgroundColor: `${def.colorHex}22` },
        ]}>
        <VaultChestIcon size={42} />
      </View>

      <View style={[styles.text, isPhone && styles.textPhone]}>
        <Text style={styles.eyebrow}>{def.title}</Text>
        <Text style={[styles.title, isPhone && styles.titlePhone]}>{title}</Text>

        <View style={styles.progressRow}>
          <Text style={styles.progressLabel}>
            {progress.ownedTotal} / {progress.total} DISCOVERED
          </Text>
          <View style={styles.track}>
            <View
              style={[
                styles.fill,
                { width: `${progress.pct}%` },
                webOnly({ backgroundImage: 'linear-gradient(90deg, #8B5CF6, #5B2FD9)' }),
              ]}
            />
          </View>
          <Text style={styles.pct}>{progress.pct}%</Text>
        </View>

        <View style={styles.chips}>
          {progress.tiers.map(t => (
            <View
              key={t.key}
              style={[
                styles.chip,
                isPhone && styles.chipPhone,
                { borderColor: `${t.color}70` },
              ]}>
              <Text style={[styles.chipText, { color: t.color }]}>
                {t.label} {t.owned}/{t.total}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    ...panelSurface,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: 'rgba(139,111,255,0.30)',
    borderRadius: 22,
    paddingVertical: 22,
    paddingHorizontal: 26,
  },
  headerTablet: {
    padding: 18,
  },
  headerPhone: {
    flexWrap: 'wrap',
  },
  companion: {
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  icon: {
    width: 74,
    height: 74,
    borderRadius: 20,
    marginLeft: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    flex: 1,
    minWidth: 0,
    marginLeft: 20,
  },
  textPhone: {
    flexBasis: '100%',
    marginLeft: 0,
    marginTop: 16,
  },
  eyebrow: {
    ...bodyFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.12, 11),
    textTransform: 'uppercase',
    color: COLORS.muted,
  },
  title: {
    ...headingFont('900'),
    fontSize: TYPE['2xl'],
    letterSpacing: em(0.01, 25),
    textTransform: 'uppercase',
    color: COLORS.text,
    marginTop: 3,
    marginBottom: 12,
  },
  titlePhone: {
    fontSize: TYPE.lg,
    fontFamily: FONTS.heading,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  progressLabel: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.03, 11.5),
    color: COLORS.gold,
  },
  track: {
    flex: 1,
    height: 8,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
    marginHorizontal: 12,
  },
  fill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: COLORS.accent,
  },
  pct: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    color: COLORS.text,
    minWidth: 34,
    textAlign: 'right',
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginRight: -7,
    marginBottom: -7,
  },
  chip: {
    borderWidth: 1.5,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 11,
    marginRight: 7,
    marginBottom: 7,
  },
  chipPhone: {
    marginRight: 4,
    marginBottom: 4,
  },
  chipText: {
    ...bodyFont('700'),
    fontSize: TYPE.xs,
  },
});

export default VaultHeader;
